// Order sets, apply side (PROD-2712): the rail's "Your order sets" list — board 5 (ApRail) of the
// v3 design. One row per active set: its name, then "<n> orders (<k> optional) · <codes> · matches",
// an AC4 line when some of its orders left the catalogue, and a ＋ that applies it. Each row is
// also a drag source: drop it onto a diagnosis to apply the set to that diagnosis.
//
// Rendered by the DS `ApSearchRail` through its `hostSubTab` (Signal DS 2.13.0), behind the rail's
// own ICD-10 | Order sets toggle. The rows are local, against tokens (a graduation candidate); their
// styles mirror the DS rail's own (13px name, 12px hint meta, the brand-blue ＋).
//
// DRAG PAYLOAD. The canvas only accepts the DS `AP_DRAG_MIME` kinds (a closed union with no
// "order set"), so a set rides as an ordinary A/P drag with a marked `item`. The A/P view checks
// the marker FIRST, before any order-kind handling, so a set is never mistaken for an order.
import { setApDragData } from '@scripsteam/scrips-signal-ds';
import { Plus } from 'lucide-react';
import { notOrderableCountOf, type OrderSetApplySummary } from '@/types/order-set-apply-api';
import { useApplyOrderSets } from './order-set-apply-service';
import { notOrderableLine } from './order-set-apply';
import { orderSetMetaLine, orderSetsForRail, type OrderSetDragItem } from './order-sets-rail-model';

const HINT = 'm-0 pt-2 px-0 pb-1 text-center text-sm text-text-muted';

export interface OrderSetsRailListProps {
  sets: OrderSetApplySummary[];
  loading?: boolean;
  error?: boolean;
  query: string;
  /** The visit's diagnosis codes (for "matches" and the sort). */
  visitCodes: string[];
  /** ＋ — apply with no target diagnosis. Absent → no ＋ and no drag. */
  onApply?: (set: OrderSetApplySummary) => void;
  /** The set being fetched for an apply (its ＋ is busy; one apply at a time). */
  applyingId?: string | null;
}

export function OrderSetsRailList({ sets, loading, error, query, visitCodes, onApply, applyingId }: OrderSetsRailListProps) {
  if (loading) return <p data-slot="order-sets-hint" className={HINT} role="status" aria-live="polite">Searching…</p>;
  if (error) return <p data-slot="order-sets-hint" className={HINT} role="status" aria-live="polite">Couldn’t load your order sets.</p>;
  const rows = orderSetsForRail(sets, visitCodes, query);
  if (rows.length === 0) {
    return (
      <p data-slot="order-sets-hint" className={HINT} role="status" aria-live="polite">
        {sets.length > 0 ? 'No order set found' : 'No order sets yet'}
      </p>
    );
  }
  return (
    <ul data-slot="order-sets-list" className="list-none m-0 p-0" aria-label="Your order sets">
      {rows.map(({ set, matches }) => {
        const blocked = notOrderableCountOf(set);
        const busy = applyingId === set.id;
        return (
          <li
            key={set.id}
            data-slot="order-set-row"
            draggable={!!onApply}
            onDragStart={
              onApply
                ? (e) =>
                    setApDragData(e, {
                      // See the file header: the DS drag kinds are closed, so the set is marked.
                      kind: 'lab',
                      item: { __orderSet: true, id: set.id, name: set.name } satisfies OrderSetDragItem,
                      label: set.name,
                    })
                : undefined
            }
            className="flex items-center justify-between gap-2 py-2 px-0 border-b border-solid border-0 border-border-subtle"
          >
            <span className="flex-1 min-w-0">
              <span data-slot="order-set-name" className="block text-sm text-text-primary">
                {set.name}
              </span>
              <span data-slot="order-set-meta" className="block text-size-xs text-text-hint">
                {orderSetMetaLine(set, matches)}
              </span>
              {blocked > 0 && (
                <span data-slot="order-set-warning" className="block text-size-xs text-status-warning">
                  {notOrderableLine(blocked)}
                </span>
              )}
            </span>
            {onApply && (
              <button
                type="button"
                data-slot="order-set-add"
                aria-label={`Add ${set.name}`}
                aria-busy={busy || undefined}
                disabled={!!applyingId}
                onClick={() => onApply(set)}
                className="icon-action cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="size-4" aria-hidden />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export type OrderSetsRailSectionProps = Omit<OrderSetsRailListProps, 'sets' | 'loading' | 'error'>;

/**
 * The body of the rail's "Order sets" sub-tab (the DS `hostSubTab`): fetches the caller's active
 * sets (GET ?status=active) and lists them. Mounted ONLY while that sub-tab shows, so the flag-off
 * rail — and the ICD-10 sub-tab — run no extra query at all.
 */
export function OrderSetsRailSection(props: OrderSetsRailSectionProps) {
  const query = useApplyOrderSets(true);
  return <OrderSetsRailList {...props} sets={query.data ?? []} loading={query.isLoading} error={query.isError} />;
}
